import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { convertToUTC } from '../../../lib/convert';
import type { DateRange } from '../../shared';
import { toPendingRows } from '../lib/pendingRows';
import { getNotStarted } from './pendingApi';

export const pendingKeys = {
    all: ['pending'] as const,
    lists: () => [...pendingKeys.all, 'list'] as const,
    list: (start: string, end: string) => [...pendingKeys.lists(), start, end] as const,
};

// The backend has no server-side filters or paging for not-started records, so
// fetch the whole range once and let the page filter and paginate in memory.
const FETCH_LIMIT = 1000;

/**
 * Not-started records in `range` as table rows, newest first.
 *
 * The cache holds the raw API records; `select` maps them to rows. Mutations can
 * then patch a record in the cache (e.g. an optimistic note edit) in the API's own
 * shape, and every table reading it re-derives its rows.
 */
export const usePendingQuery = ({ start, end }: DateRange) => {
    const startUtc = convertToUTC(start) as string;
    const endUtc = end ? (convertToUTC(end) as string) : 'now';

    return useQuery({
        queryKey: pendingKeys.list(startUtc, endUtc),
        queryFn: async (): Promise<any[]> => {
            const to = end ? endUtc : (convertToUTC(dayjs()) as string);
            const data = await getNotStarted(0, FETCH_LIMIT, startUtc, to);
            return data?.records || data?.data?.records || [];
        },
        select: toPendingRows,
    });
};
